import { GRAVITY, WATER_Y } from '@tanky/shared';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  gravity: number;
  glow?: boolean;
  /** Růst velikosti za sekundu (kouř). */
  grow?: number;
  alpha?: number;
  /** Hloubka ve 3D (výbuch kamikadze dronu mimo terén); 2D ji ignoruje. */
  z?: number;
}

/** Zrnko sesuvu půdy. */
interface Grain {
  fx: number;
  fy: number;
  tx: number;
  ty: number;
  dur: number;
  delay: number;
  color: string;
}

interface Slide {
  grains: Grain[];
  t: number;
  total: number;
  onDone: () => void;
}

/** Víc zrnek animovat nemá smysl – zbytek se jen odkryje na konci. */
const MAX_SLIDE_GRAINS = 900;

/** Světlice / raketa ohňostroje. */
interface Rocket {
  kind: 'flare' | 'firework';
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
}

interface Ring {
  x: number;
  y: number;
  r: number;
  life: number;
  max: number;
  color: string;
  z?: number;
}

interface Floater {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

const FIRE = ['#fff3b0', '#ffd166', '#ff9f43', '#ff6b3d', '#e8412c'];

export class Effects {
  particles: Particle[] = [];
  rings: Ring[] = [];
  floaters: Floater[] = [];
  rockets: Rocket[] = [];
  slides: Slide[] = [];
  /** Aktivní černé díry. */
  holes: { x: number; y: number; t: number; dur: number }[] = [];
  /** Blesky (klikatá čára, krátce). */
  bolts: { x: number; y0: number; y1: number; t: number; seed: number }[] = [];
  /** Zbývající čas zemětřesení. */
  quakeLeft = 0;

  blackhole(x: number, y: number, dur: number) {
    this.holes.push({ x, y, t: 0, dur });
  }

  lightning(x: number, y0: number, y1: number) {
    this.bolts.push({ x, y0, y1, t: 0, seed: Math.random() * 1000 });
    this.flash = Math.max(this.flash, 0.55);
    this.shake = Math.max(this.shake, 8);
    for (let i = 0; i < 24; i++) {
      const a = -Math.PI * Math.random();
      const s = 60 + Math.random() * 160;
      this.particles.push({ x, y: y1 - 4, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 0, max: 0.5, size: 2, color: '#fff7b0', gravity: 0.4, glow: true });
    }
  }

  quake(dur: number) {
    this.quakeLeft = Math.max(this.quakeLeft, dur);
  }

  /**
   * Animace sesuvu: zrnka letí z původního místa na cílové (vodorovně rovnoměrně,
   * svisle se zrychlením). Po dopadu všech se zavolá onDone (odkrytí terénu).
   */
  slide(moves: number[], colors: Uint32Array, onDone: () => void) {
    const n = moves.length / 4;
    const step = Math.max(1, Math.ceil(n / MAX_SLIDE_GRAINS));
    const grains: Grain[] = [];
    let total = 0;
    for (let i = 0; i < n; i += step) {
      const [fx, fy, tx, ty] = [moves[i * 4], moves[i * 4 + 1], moves[i * 4 + 2], moves[i * 4 + 3]];
      const dur = 0.25 + Math.min(0.35, Math.hypot(tx - fx, ty - fy) / 300);
      // Zrnka výš ve svahu se utrhnou o chlup později – vypadá to jako lavina
      const delay = 0.12 + ((i * 7919) % 100) / 100 * 0.12;
      total = Math.max(total, delay + dur);
      const c = colors[i];
      grains.push({ fx, fy, tx, ty, dur, delay, color: `rgb(${c >> 16},${(c >> 8) & 255},${c & 255})` });
    }
    this.slides.push({ grains, t: 0, total, onDone });
    // Trocha prachu nad místem sesuvu
    for (let i = 0; i < Math.min(18, grains.length); i++) {
      const g = grains[Math.floor((i * grains.length) / 18)];
      this.particles.push({ x: g.fx, y: g.fy, vx: (Math.random() - 0.5) * 20, vy: -10 - Math.random() * 15, life: -Math.random() * 0.3, max: 1 + Math.random() * 0.6, size: 3 + Math.random() * 3, grow: 5, alpha: 0.35, color: 'rgb(160,130,100)', gravity: -0.01 });
    }
  }
  shake = 0;
  /** Bílý záblesk přes celou obrazovku (atomovka), 0–1. */
  flash = 0;

  /** z = hloubka ve 3D (jen kamikadze dron mimo plát terénu). */
  explosion(x: number, y: number, r: number, dirt = false, dirtColor = '#8a5a3b', z = 0) {
    const p0 = this.particles.length;
    const r0 = this.rings.length;
    this.explosionAt(x, y, r, dirt, dirtColor);
    if (z) {
      for (let i = p0; i < this.particles.length; i++) this.particles[i].z = z;
      for (let i = r0; i < this.rings.length; i++) this.rings[i].z = z;
    }
  }

  private explosionAt(x: number, y: number, r: number, dirt: boolean, dirtColor: string) {
    if (r >= 80 && !dirt) this.mushroom(x, y, r);
    if (dirt) {
      for (let i = 0; i < 40; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = Math.random() * r * 3;
        this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 60, life: 0, max: 0.6 + Math.random() * 0.5, size: 2 + Math.random() * 3, color: dirtColor, gravity: 1 });
      }
      this.shake = Math.max(this.shake, 3);
      return;
    }
    this.rings.push({ x, y, r, life: 0, max: 0.35, color: '#fff3b0' });
    const n = Math.min(90, 20 + r * 1.2);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = (0.3 + Math.random()) * r * 4;
      this.particles.push({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 0,
        max: 0.3 + Math.random() * 0.5,
        size: 2 + Math.random() * 3,
        color: FIRE[Math.floor(Math.random() * FIRE.length)],
        gravity: 0.2,
        glow: true,
      });
    }
    for (let i = 0; i < n / 2; i++) {
      const a = -Math.PI * Math.random();
      const s = Math.random() * r * 5;
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 0, max: 0.8 + Math.random() * 0.8, size: 1.5 + Math.random() * 2, color: dirtColor, gravity: 1 });
    }
    for (let i = 0; i < 8; i++) {
      this.particles.push({ x: x + (Math.random() - 0.5) * r, y: y + (Math.random() - 0.5) * r, vx: (Math.random() - 0.5) * 20, vy: -20 - Math.random() * 30, life: 0, max: 1.2 + Math.random(), size: r * 0.25 + Math.random() * r * 0.2, color: 'rgba(60,60,70,0.35)', gravity: -0.02 });
    }
    this.shake = Math.max(this.shake, Math.min(14, r / 4));
  }

  /** Obrovský výbuch: záblesk a houbový mrak. */
  private mushroom(x: number, y: number, r: number) {
    this.flash = 1;
    this.shake = Math.max(this.shake, 22);
    for (let i = 0; i < 60; i++) {
      const up = Math.random();
      // Sloupec a klobouk houby
      const cap = up > 0.55;
      this.particles.push({
        x: x + (Math.random() - 0.5) * (cap ? r * 1.4 : r * 0.35),
        y: y - (cap ? r * 1.2 + Math.random() * r * 0.5 : Math.random() * r * 1.1),
        vx: (Math.random() - 0.5) * (cap ? 40 : 12),
        vy: -25 - Math.random() * 30,
        life: -Math.random() * 0.4,
        max: 2.2 + Math.random() * 1.2,
        size: r * (cap ? 0.18 : 0.12) + Math.random() * r * 0.1,
        grow: 10,
        alpha: 0.55,
        color: cap ? (Math.random() < 0.4 ? 'rgb(255,150,70)' : 'rgb(90,70,70)') : 'rgb(120,100,90)',
        gravity: -0.02,
      });
    }
  }

  /** Rudý dýmový signál – cíl leteckého úderu. */
  marker(x: number, y: number) {
    for (let i = 0; i < 26; i++) {
      this.particles.push({ x: x + (Math.random() - 0.5) * 6, y: y - 4, vx: (Math.random() - 0.5) * 14, vy: -40 - Math.random() * 50, life: -i * 0.05, max: 1.8 + Math.random(), size: 4 + Math.random() * 3, grow: 6, alpha: 0.7, color: i % 3 ? 'rgb(230,60,50)' : 'rgb(255,140,120)', gravity: -0.03 });
    }
  }

  /** Fialové jiskry teleportu. */
  teleport(x: number, y: number) {
    this.rings.push({ x, y, r: 26, life: 0, max: 0.45, color: '#c77dff' });
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      const sp = 60 + Math.random() * 90;
      this.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0, max: 0.6 + Math.random() * 0.4, size: 2.2, color: i % 2 ? '#e0aaff' : '#9d4edd', gravity: -0.1, glow: true });
    }
  }

  splash(x: number) {
    for (let i = 0; i < 30; i++) {
      this.particles.push({ x: x + (Math.random() - 0.5) * 10, y: WATER_Y, vx: (Math.random() - 0.5) * 120, vy: -120 - Math.random() * 220, life: 0, max: 0.9, size: 2 + Math.random() * 2, color: '#9fd4ff', gravity: 1 });
    }
  }

  damage(x: number, y: number, amount: number) {
    this.floaters.push({ x, y, text: `−${amount}`, color: '#ff8a8a', life: 0 });
  }

  label(x: number, y: number, text: string, color = '#fff') {
    this.floaters.push({ x, y, text, color, life: 0 });
  }

  smoke(x: number, y: number) {
    this.particles.push({ x, y, vx: (Math.random() - 0.5) * 10, vy: -25 - Math.random() * 15, life: 0, max: 1.6, size: 3 + Math.random() * 3, color: 'rgba(40,40,48,0.4)', gravity: -0.02 });
  }

  /** Světlice: vystřelí vzhůru, pak se pomalu snáší a svítí. */
  flare(x: number, y: number) {
    this.rockets.push({ kind: 'flare', x, y: y - 10, vx: (Math.random() - 0.5) * 40, vy: -330, life: 0, max: 6, color: '#ff4d3d' });
  }

  firework(x: number, y: number) {
    this.rockets.push({ kind: 'firework', x, y: y - 10, vx: (Math.random() - 0.5) * 30, vy: -300, life: 0, max: 0.75, color: '#fff' });
  }

  smokeScreen(x: number, y: number) {
    for (let i = 0; i < 46; i++) {
      const a = Math.random() * Math.PI * 2;
      const s = 20 + Math.random() * 50;
      const shade = 150 + Math.floor(Math.random() * 60);
      this.particles.push({
        x: x + (Math.random() - 0.5) * 20,
        y: y - 8 + (Math.random() - 0.5) * 10,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s * 0.5 - 12,
        life: -Math.random() * 0.6,
        max: 5 + Math.random() * 2.5,
        size: 10 + Math.random() * 10,
        grow: 9,
        alpha: 0.75,
        color: `rgb(${shade},${shade},${shade + 8})`,
        gravity: -0.01,
      });
    }
  }

  private burst(x: number, y: number) {
    const palette = ['#ff5c6c', '#ffd166', '#4cd18a', '#5ab8ff', '#c77dff', '#ffffff'];
    const base = palette[Math.floor(Math.random() * palette.length)];
    for (let i = 0; i < 70; i++) {
      const a = (i / 70) * Math.PI * 2;
      const s = 90 + Math.random() * 60;
      this.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 0, max: 1.2 + Math.random() * 0.6, size: 2.5, color: Math.random() < 0.25 ? '#fff' : base, gravity: 0.25, glow: true });
    }
  }

  /** Otřes kamery (px) – výbuchy + zemětřesení. */
  get shakeAmount() {
    return Math.max(this.shake, this.quakeLeft > 0 ? 6 + this.quakeLeft * 6 : 0);
  }

  get busy() {
    return this.particles.length > 0 || this.rings.length > 0 || this.rockets.length > 0 || this.slides.length > 0 || this.holes.length > 0;
  }

  update(dt: number) {
    for (const r of this.rockets) {
      r.life += dt;
      if (r.kind === 'flare') {
        // Po vystoupání padák – pomalé snášení
        r.vy = r.vy < 30 ? r.vy + GRAVITY * 0.9 * dt : 30;
        if (Math.random() < dt * 30) {
          this.particles.push({ x: r.x, y: r.y, vx: (Math.random() - 0.5) * 30, vy: 20 + Math.random() * 30, life: 0, max: 0.6, size: 2, color: '#ffb199', gravity: 0.3, glow: true });
        }
      } else {
        r.vy += GRAVITY * 0.3 * dt;
        if (Math.random() < dt * 60) {
          this.particles.push({ x: r.x, y: r.y, vx: (Math.random() - 0.5) * 20, vy: 30, life: 0, max: 0.4, size: 1.8, color: '#ffd166', gravity: 0.2, glow: true });
        }
        if (r.life >= r.max) this.burst(r.x, r.y);
      }
      r.x += r.vx * dt;
      r.y += r.vy * dt;
    }
    this.rockets = this.rockets.filter((r) => r.life < r.max);
    for (const s of this.slides) {
      s.t += dt;
      if (s.t >= s.total) s.onDone();
    }
    this.slides = this.slides.filter((s) => s.t < s.total);
    for (const p of this.particles) {
      p.life += dt;
      if (p.life < 0) continue;
      if (p.grow) p.size += p.grow * dt;
      p.vy += GRAVITY * p.gravity * dt;
      p.vx *= 1 - dt * 1.5;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);
    for (const r of this.rings) r.life += dt;
    this.rings = this.rings.filter((r) => r.life < r.max);
    for (const f of this.floaters) {
      f.life += dt;
      f.y -= 30 * dt;
    }
    this.floaters = this.floaters.filter((f) => f.life < 1.6);
    this.shake = Math.max(0, this.shake - dt * 30);
    this.flash = Math.max(0, this.flash - dt * 1.6);
    this.quakeLeft = Math.max(0, this.quakeLeft - dt);
    for (const h of this.holes) {
      h.t += dt;
      // Úlomky nasávané do díry
      if (h.t < h.dur && Math.random() < dt * 40) {
        const a = Math.random() * Math.PI * 2;
        const r = 60 + Math.random() * 120;
        this.particles.push({ x: h.x + Math.cos(a) * r, y: h.y + Math.sin(a) * r, vx: -Math.cos(a) * r * 1.6, vy: -Math.sin(a) * r * 1.6, life: 0, max: 0.6, size: 2, color: Math.random() < 0.5 ? '#b388ff' : '#8a5a3b', gravity: 0 });
      }
    }
    this.holes = this.holes.filter((h) => h.t < h.dur + 0.4);
    for (const b of this.bolts) b.t += dt;
    this.bolts = this.bolts.filter((b) => b.t < 0.45);
  }

  draw(ctx: CanvasRenderingContext2D) {
    for (const h of this.holes) {
      const grow = Math.min(1, h.t / 0.4) * (h.t > h.dur ? Math.max(0, 1 - (h.t - h.dur) / 0.4) : 1);
      const R = 34 * grow;
      if (R <= 0.5) continue;
      ctx.save();
      ctx.translate(h.x, h.y);
      // Akreční disk
      ctx.rotate(h.t * 4);
      for (let i = 0; i < 3; i++) {
        ctx.strokeStyle = i % 2 ? 'rgba(179,136,255,0.55)' : 'rgba(255,170,90,0.45)';
        ctx.lineWidth = 3 - i * 0.6;
        ctx.beginPath();
        ctx.ellipse(0, 0, R * (1.6 + i * 0.35), R * (0.55 + i * 0.12), i * 0.9, 0, Math.PI * 2);
        ctx.stroke();
      }
      const g = ctx.createRadialGradient(0, 0, R * 0.2, 0, 0, R);
      g.addColorStop(0, '#000');
      g.addColorStop(0.75, '#05020a');
      g.addColorStop(1, 'rgba(120,80,200,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    for (const b of this.bolts) {
      const a = 1 - b.t / 0.45;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // Klikatá dráha – stejná po celou dobu života blesku
      const pts: number[] = [];
      const n = 14;
      for (let i = 0; i <= n; i++) {
        const k = i / n;
        const jitter = i === 0 || i === n ? 0 : Math.sin(b.seed + i * 12.9898) * 14;
        pts.push(b.x + jitter, b.y0 + (b.y1 - b.y0) * k);
      }
      for (const [w, col] of [
        [9, `rgba(150,170,255,${0.35 * a})`],
        [4, `rgba(210,225,255,${0.8 * a})`],
        [1.6, `rgba(255,255,255,${a})`],
      ] as const) {
        ctx.strokeStyle = col;
        ctx.lineWidth = w;
        ctx.beginPath();
        ctx.moveTo(pts[0], pts[1]);
        for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
        ctx.stroke();
      }
      ctx.restore();
    }
    for (const s of this.slides) {
      for (const g of s.grains) {
        const k = Math.max(0, Math.min(1, (s.t - g.delay) / g.dur));
        if (k >= 1) continue; // už dopadlo – ukáže se v terénu
        ctx.fillStyle = g.color;
        ctx.fillRect(g.fx + (g.tx - g.fx) * k, g.fy + (g.ty - g.fy) * k * k, 1.15, 1.15);
      }
    }
    if (this.rockets.length) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (const r of this.rockets) {
        const fade = r.kind === 'flare' ? Math.min(1, (r.max - r.life) / 1.2) : 1;
        const flick = 0.85 + Math.random() * 0.15;
        const R = r.kind === 'flare' ? 110 : 18;
        const g = ctx.createRadialGradient(r.x, r.y, 0, r.x, r.y, R);
        g.addColorStop(0, `rgba(255,120,90,${0.55 * fade * flick})`);
        g.addColorStop(0.3, `rgba(255,80,60,${0.2 * fade})`);
        g.addColorStop(1, 'rgba(255,60,40,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(r.x, r.y, R, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = `rgba(255,245,230,${fade})`;
        ctx.beginPath();
        ctx.arc(r.x, r.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    for (const r of this.rings) {
      const k = r.life / r.max;
      ctx.globalAlpha = 1 - k;
      ctx.fillStyle = r.color;
      ctx.beginPath();
      ctx.arc(r.x, r.y, r.r * (0.5 + k * 0.7), 0, Math.PI * 2);
      ctx.fill();
    }
    for (const p of this.particles) {
      if (p.life < 0) continue;
      const k = 1 - p.life / p.max;
      if (p.grow) {
        // Kulatý kouř s měkkým okrajem
        ctx.globalAlpha = Math.max(0, Math.min(1, k * 1.6)) * (p.alpha ?? 1);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.globalAlpha = Math.max(0, k);
      ctx.fillStyle = p.color;
      const s = p.glow ? p.size * (0.5 + k) : p.size;
      ctx.fillRect(p.x - s / 2, p.y - s / 2, s, s);
    }
    ctx.globalAlpha = 1;
  }
}
